// @ts-strict-check
import React, { useMemo, useRef, useState, useCallback } from 'react';
import { LoadingSpinner, ErrorMessage, EmptyState, ColumnInfoTooltip, CustomSelect } from '../ui';
import ChartControls from '../ui/ChartControls';
import { useMarketPulseData } from '../../hooks/useClubSections';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { TimePeriod } from '../../types/api';
import {
  KPI_COMPARISON_ROLE_LABEL,
  MARKET_PULSE_DASHBOARD,
  MARKET_PULSE_DASHBOARD_SOURCE,
  MARKET_PULSE_KPI,
  MARKET_PULSE_KPI_ID,
  MARKET_PULSE_KPI_TITLE,
  MARKET_PULSE_LABELS,
  MARKET_PULSE_SCREENSHOT_COLORS,
  ComparisonStatus,
  KPI_CARD_LABELS,
  KPI_METRIC_ORDER,
  MetricKey,
  PulseRow,
  formatKpiComparisonMonthDetail,
  formatKpiDiffCardTitle,
  formatKpiExcludedMonthDetail,
} from './marketPulseConstants';
import { fmtDiff, fmtPctCompact, STATUS_CONFIG } from './marketPulseFormatting';
import {
  computeMarketPulseKpiData,
  getDefaultKpiPeriod,
  KpiDataNoticeItem,
  MarketPulseKpiData,
  sanitizeKpiPeriodKeys,
} from './marketPulseKpiData';
import { sliceClubDisplayWindow, sortRowsChronologically } from '../../utils/clubGrowthMetrics';
import ConfidenceIndexBadge from '../shared/ConfidenceIndexBadge';
import { computeInvoiceCountConfidence } from '../../utils/confidenceIndex';

const Num: React.FC<{ children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({
  children,
  className,
  style,
}) => (
  <span dir="ltr" className={`inline-block tabular-nums ${className ?? ''}`} style={style}>{children}</span>
);

const LegendDot: React.FC<{ color: string }> = ({ color }) => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 10 10"
    className="shrink-0"
    aria-hidden
    style={{ display: 'block' }}
  >
    <circle cx="5" cy="5" r="5" fill={color} />
  </svg>
);

const KpiNoticeMonthCard: React.FC<{
  item: KpiDataNoticeItem;
  caption: string;
  tone: 'warning' | 'neutral';
  detail: string;
}> = ({ item, caption, tone, detail }) => {
  const coveragePercent =
    item.fullDays > 0
      ? Math.min(100, Math.max(0, Math.round((item.orderDays / item.fullDays) * 100)))
      : 0;

  return (
  <div
    className={`rounded-md px-3 py-2.5 min-w-0 flex flex-col gap-1.5 ${
      tone === 'warning' ? 'bg-status-warning/10' : 'bg-bg-secondary/55'
    }`}
    title={detail}
  >
    <div className="flex items-center justify-between gap-2">
      <p className="text-base font-bold text-text-primary leading-none">{item.monthLabel}</p>
      <span
        className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-[11px] font-medium ${
          tone === 'warning'
            ? 'bg-status-warning/15 text-status-warning'
            : 'bg-bg-card/80 text-text-tertiary'
        }`}
        style={{ lineHeight: 1.25 }}
      >
        {tone === 'warning' ? MARKET_PULSE_KPI.noticeIncomplete : MARKET_PULSE_KPI.noticeExcluded}
      </span>
    </div>
    <div className="flex items-center gap-2 w-full min-w-0" dir="ltr">
      <div
        className="flex-1 min-w-[4.5rem] h-2 rounded-full overflow-hidden"
        style={{ backgroundColor: MARKET_PULSE_SCREENSHOT_COLORS.progressTrack }}
        role="progressbar"
        aria-valuenow={item.orderDays}
        aria-valuemin={0}
        aria-valuemax={item.fullDays}
        aria-label={`${item.orderDays} ${MARKET_PULSE_LABELS.daysUnit} از ${item.fullDays}`}
      >
        <div
          className="h-full rounded-full transition-[width] duration-300"
          style={{
            width: `${coveragePercent}%`,
            backgroundColor:
              tone === 'warning'
                ? MARKET_PULSE_SCREENSHOT_COLORS.warning
                : MARKET_PULSE_SCREENSHOT_COLORS.neutral,
          }}
        />
      </div>
      <span className="text-xs tabular-nums text-text-secondary font-medium shrink-0 whitespace-nowrap">
        <Num>{item.orderDays}/{item.fullDays}</Num> {MARKET_PULSE_LABELS.daysUnit}
      </span>
    </div>
    <p className="text-xs text-text-secondary leading-relaxed">{caption}</p>
  </div>
  );
};

const KpiDataNoticePanel: React.FC<{
  comparisonItems: KpiDataNoticeItem[];
  excludedItems: KpiDataNoticeItem[];
  showEstimationNotice?: boolean;
}> = ({ comparisonItems, excludedItems, showEstimationNotice = false }) => {
  const hasComparison = comparisonItems.length > 0;
  const hasExcluded = excludedItems.length > 0;
  const columnCount = [hasComparison, hasExcluded].filter(Boolean).length;

  return (
    <div
      className="rounded-xl border border-status-warning/20 overflow-hidden shadow-sm bg-bg-card"
      role="alert"
    >
      <div
        className="flex flex-col items-center justify-center gap-1.5 px-4 py-3 border-b border-status-warning/25 text-center bg-status-warning-bg"
      >
        <div className="flex items-center justify-center gap-2">
          {/* <AlertTriangle className="w-4 h-4 shrink-0 text-status-warning" strokeWidth={2} aria-hidden="true" /> */}
          <p className="text-sm font-semibold text-text-primary leading-snug">{MARKET_PULSE_KPI.noticePanelTitle}</p>
        </div>
        {showEstimationNotice && (
          <p className="text-xs text-text-secondary leading-relaxed max-w-2xl">
            {MARKET_PULSE_KPI.estimationNoticePrefix}{' '}
            <strong className="font-semibold text-text-primary">{MARKET_PULSE_LABELS.estimated}</strong>{' '}
            {MARKET_PULSE_KPI.estimationNoticeSuffix}
          </p>
        )}
      </div>

      <div className="p-4 space-y-6 bg-bg-secondary">
        <div
          className={`grid grid-cols-1 gap-3 ${
            columnCount >= 2 ? 'lg:grid-cols-2' : ''
          }`}
        >
          {hasComparison && (
            <div
              data-screenshot-notice-column
              className="relative overflow-hidden rounded-lg bg-bg-card px-4 py-3 flex flex-col gap-2 min-h-[7.5rem]"
            >
              <span
                className="absolute inset-y-0 right-0 w-[3px] pointer-events-none"
                style={{ backgroundColor: MARKET_PULSE_SCREENSHOT_COLORS.warning }}
                aria-hidden
              />
              <p data-screenshot-notice-title className="text-xs font-semibold text-status-warning whitespace-nowrap">
                {MARKET_PULSE_KPI.noticeComparisonMonths}
              </p>
              <div className="flex flex-col gap-2 mt-auto">
                {comparisonItems.map((item) => (
                  <KpiNoticeMonthCard
                    key={item.id}
                    item={item}
                    tone="warning"
                    caption={KPI_COMPARISON_ROLE_LABEL[item.role as 'period-start' | 'period-end']}
                    detail={formatKpiComparisonMonthDetail(item.monthLabel, item.orderDays, item.fullDays)}
                  />
                ))}
              </div>
            </div>
          )}

          {hasExcluded && (
            <div
              data-screenshot-notice-column
              className="relative overflow-hidden rounded-lg bg-bg-card px-4 py-3 flex flex-col gap-2 min-h-[7.5rem]"
            >
              <span
                className="absolute inset-y-0 right-0 w-[3px] pointer-events-none"
                style={{ backgroundColor: MARKET_PULSE_SCREENSHOT_COLORS.neutral }}
                aria-hidden
              />
              <p data-screenshot-notice-title className="text-xs font-semibold text-text-tertiary whitespace-nowrap">
                {MARKET_PULSE_KPI.noticeExcludedMonths}
              </p>
              <div className="flex flex-col gap-2 mt-auto">
                {excludedItems.map((item) => (
                  <KpiNoticeMonthCard
                    key={item.id}
                    item={item}
                    tone="neutral"
                    caption={MARKET_PULSE_KPI.noticeExcludedCaption}
                    detail={formatKpiExcludedMonthDetail(item.monthLabel, item.orderDays, item.fullDays)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-5 md:pt-6 border-t border-border-primary/50">
          {hasComparison && (
            <span className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-text-secondary bg-bg-card/90">
              <LegendDot color={MARKET_PULSE_SCREENSHOT_COLORS.warning} />
              <strong className="font-semibold text-text-primary">{MARKET_PULSE_LABELS.branchColon}</strong>
              {MARKET_PULSE_KPI.noticeBranchIncomplete}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-text-secondary bg-bg-card/90">
            <LegendDot color={MARKET_PULSE_SCREENSHOT_COLORS.ahead} />
            <strong className="font-semibold text-text-primary">{MARKET_PULSE_LABELS.marketColon}</strong>
            {MARKET_PULSE_KPI.noticeMarketComplete}
          </span>
        </div>
      </div>
    </div>
  );
};

/** برچسب کوتاه وضعیت برای متن داخل pill پایین کارت (به‌جای برچسب کامل «… از بازار») */
const STATUS_SHORT_LABEL: Record<ComparisonStatus, string> = {
  better: MARKET_PULSE_KPI.ahead,
  worse: MARKET_PULSE_KPI.behind,
  equal: 'هم‌تراز',
};

const DiffPill: React.FC<{
  status: ComparisonStatus;
  text: string;
  title?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  hideIcon?: boolean;
}> = ({ status, text, title, size = 'sm', fullWidth = false, hideIcon = false }) => {
  const cfg = STATUS_CONFIG[status];
  const isLarge = size === 'lg';
  const isMedium = size === 'md';

  const pillHeight = isLarge ? 44 : isMedium ? 36 : 28;
  const fontSize = isLarge ? 20 : isMedium ? 16 : 12;
  const horizontalPadding = isLarge ? 16 : isMedium ? 12 : 8;
  const borderWidth = isLarge || isMedium ? 0 : 1;
  const borderRadius = fullWidth && isLarge ? 9999 : isLarge ? 12 : isMedium ? 8 : 9999;
  const innerHeight = pillHeight - borderWidth * 2;

  return (
    <div
      dir="ltr"
      data-screenshot-pill
      data-screenshot-pill-size={size}
      data-screenshot-pill-color={cfg.color}
      className={`relative z-10 tabular-nums font-bold text-center whitespace-nowrap screenshot-diff-pill ${
        fullWidth ? 'w-full' : 'mx-auto'
      }`}
      style={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        border: borderWidth > 0 ? `${borderWidth}px solid ${cfg.border}` : 'none',
        borderRadius,
        boxSizing: 'border-box',
        height: pillHeight,
        minWidth: isLarge && !fullWidth ? 112 : undefined,
        paddingLeft: horizontalPadding,
        paddingRight: horizontalPadding,
        fontSize,
        lineHeight: `${innerHeight}px`,
        fontFamily: 'Arial, Helvetica, sans-serif',
      }}
      title={title}
    >
      {hideIcon ? text : `${text}\u00A0${cfg.icon}`}
    </div>
  );
};

const ComparisonKpiCard: React.FC<{
  metricKey: MetricKey;
  title: string;
  titleTooltip?: string;
  branchGrowth: number | null;
  marketGrowth: number | null;
  lead: number | null;
  status: ComparisonStatus;
  extrapolationNote?: string;
  className?: string;
}> = ({ metricKey, title, titleTooltip, branchGrowth, marketGrowth, lead, status, extrapolationNote, className }) => {
  const cfg = STATUS_CONFIG[status];
  const displayTitle = KPI_CARD_LABELS[metricKey] ?? title;
  const hasComparison = lead !== null && !Number.isNaN(lead);
  const pillText = hasComparison ? `${fmtDiff(lead)} ${STATUS_SHORT_LABEL[status]}` : fmtDiff(lead);

  return (
    <div
      className={`rounded-2xl border border-teal-200 bg-bg-card p-5 sm:p-6 flex flex-col items-center text-center shadow-sm gap-5 ${className ?? ''}`}
    >
      <div className="min-w-0 w-full">
        <div className="inline-flex items-center justify-center gap-1.5 max-w-full">
          <h3 className="m-0 text-base font-bold text-text-primary leading-snug">{displayTitle}</h3>
          {titleTooltip && <ColumnInfoTooltip text={titleTooltip} />}
        </div>
        {extrapolationNote && (
          <p className="text-[10px] text-status-warning mt-2 leading-snug">{extrapolationNote}</p>
        )}
      </div>

      <div
        className="grid w-fit mx-auto grid-cols-[auto_auto] items-baseline gap-x-1.5 gap-y-6 sm:gap-y-7"
        dir="rtl"
      >
        <Num className="justify-self-end text-2xl sm:text-[1.75rem] font-extrabold text-teal-600 leading-none">
          {fmtPctCompact(branchGrowth)}
        </Num>
        <span className="justify-self-start text-xs font-bold text-teal-600">
          {MARKET_PULSE_KPI.youLabel}
        </span>
        <Num className="justify-self-end text-2xl sm:text-[1.75rem] font-extrabold text-text-primary leading-none">
          {fmtPctCompact(marketGrowth)}
        </Num>
        <span className="justify-self-start text-xs font-bold text-text-tertiary">
          {MARKET_PULSE_LABELS.market}
        </span>
      </div>

      <div className="mt-auto w-full pt-2">
        <DiffPill
          status={status}
          text={pillText}
          title={formatKpiDiffCardTitle(cfg.label)}
          size="lg"
          fullWidth
          hideIcon
        />
      </div>
    </div>
  );
};

interface MarketPulseKpiSectionProps {
  branchId?: number;
  customTimeRange?: TimePeriod;
  extrapolateToFullMonth?: boolean;
  compact?: boolean;
  /** @deprecated Section computes its own KPI data so period controls work. Ignored when provided. */
  kpiData?: MarketPulseKpiData;
  className?: string;
}

const MarketPulseKpiSection: React.FC<MarketPulseKpiSectionProps> = ({
  branchId = 0,
  customTimeRange,
  extrapolateToFullMonth = false,
  compact = false,
  className = '',
}) => {
  const contentRef = useRef<HTMLElement>(null);
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();

  /** null = follow automatic default for the current extrapolate mode */
  const [kpiStartKey, setKpiStartKey] = useState<string | null>(null);
  const [kpiEndKey, setKpiEndKey] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useMarketPulseData(branchId, customTimeRange);

  const periodOverride = useMemo(() => {
    if (!kpiStartKey || !kpiEndKey) return null;
    return { startKey: kpiStartKey, endKey: kpiEndKey };
  }, [kpiStartKey, kpiEndKey]);

  const kpiData = useMemo(() => {
    if (!data) return null;
    const branchAll = (data.branchData as PulseRow[]) || [];
    const marketAll = (data.allRestaurantsData as PulseRow[]) || [];
    const marketPartialLatest = (data.allRestaurantsPartialLatestData as PulseRow[]) || [];
    const baseMonths = data.baseMonths ?? branchAll.length;
    return computeMarketPulseKpiData(
      branchAll,
      marketAll,
      baseMonths,
      extrapolateToFullMonth,
      periodOverride,
      marketPartialLatest,
    );
  }, [data, extrapolateToFullMonth, periodOverride]);

  const invoiceCountConfidence = useMemo(() => {
    if (!data) return null;
    const totalInvoiceCount = data.marketTotalInvoiceCount ?? 0;
    return computeInvoiceCountConfidence(totalInvoiceCount);
  }, [data]);

  const defaultPeriod = useMemo(() => {
    if (!data) return { startKey: null as string | null, endKey: null as string | null };
    const branchAll = (data.branchData as PulseRow[]) || [];
    const baseMonths = data.baseMonths ?? branchAll.length;
    const branchWindow = sliceClubDisplayWindow(sortRowsChronologically(branchAll), baseMonths);
    return getDefaultKpiPeriod(branchWindow, extrapolateToFullMonth);
  }, [data, extrapolateToFullMonth]);

  const monthOptions = kpiData?.monthOptions ?? [];

  const resolvedStartKey = kpiData?.resolvedStartKey ?? defaultPeriod.startKey;
  const resolvedEndKey = kpiData?.resolvedEndKey ?? defaultPeriod.endKey;

  // Newest-first for end-month dropdown (source list stays chronological for filtering)
  const endMonthOptions = useMemo(() => [...monthOptions].reverse(), [monthOptions]);

  // ماه ابتدا فقط ماه‌های قبل از ماه انتها (شروع ≤ انتها؛ sanitize هم start < end را نگه می‌دارد)
  // Display newest → oldest after filtering
  const startMonthOptions = useMemo(() => {
    if (!resolvedEndKey) return [...monthOptions].reverse();
    const endIdx = monthOptions.findIndex((o) => o.value === resolvedEndKey);
    if (endIdx <= 0) return [];
    return monthOptions.slice(0, endIdx).reverse();
  }, [monthOptions, resolvedEndKey]);

  const startSelectValue = useMemo(() => {
    if (!resolvedStartKey) return null;
    return monthOptions.find((o) => o.value === resolvedStartKey) ?? null;
  }, [monthOptions, resolvedStartKey]);

  const endSelectValue = useMemo(() => {
    if (!resolvedEndKey) return null;
    return monthOptions.find((o) => o.value === resolvedEndKey) ?? null;
  }, [monthOptions, resolvedEndKey]);

  const handleStartChange = useCallback(
    (option: { value: string | number; label: string } | null) => {
      if (!option || !data) return;
      const branchAll = (data.branchData as PulseRow[]) || [];
      const baseMonths = data.baseMonths ?? branchAll.length;
      const branchWindow = sliceClubDisplayWindow(sortRowsChronologically(branchAll), baseMonths);
      const nextStart = String(option.value);
      const currentEnd = kpiEndKey ?? defaultPeriod.endKey;
      if (!currentEnd) {
        setKpiStartKey(nextStart);
        return;
      }
      const sanitized = sanitizeKpiPeriodKeys(branchWindow, nextStart, currentEnd, 'end');
      setKpiStartKey(sanitized.startKey);
      setKpiEndKey(sanitized.endKey);
    },
    [data, kpiEndKey, defaultPeriod.endKey]
  );

  const handleEndChange = useCallback(
    (option: { value: string | number; label: string } | null) => {
      if (!option || !data) return;
      const branchAll = (data.branchData as PulseRow[]) || [];
      const baseMonths = data.baseMonths ?? branchAll.length;
      const branchWindow = sliceClubDisplayWindow(sortRowsChronologically(branchAll), baseMonths);
      const nextEnd = String(option.value);
      const currentStart = kpiStartKey ?? defaultPeriod.startKey;
      if (!currentStart) {
        setKpiEndKey(nextEnd);
        return;
      }
      const sanitized = sanitizeKpiPeriodKeys(branchWindow, currentStart, nextEnd, 'start');
      setKpiStartKey(sanitized.startKey);
      setKpiEndKey(sanitized.endKey);
    },
    [data, kpiStartKey, defaultPeriod.startKey]
  );


  if (isLoading) {
    return (
      <div className={`card-base card-padding flex justify-center py-12 ${className}`}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`card-base card-padding ${className}`}>
        <ErrorMessage message={MARKET_PULSE_KPI.errorFetch} onRetry={() => refetch()} />
      </div>
    );
  }

  if (!kpiData || kpiData.kpis.length === 0) {
    return (
      <div className={`card-base card-padding ${className}`}>
        <EmptyState message={MARKET_PULSE_KPI.emptyNoData} />
      </div>
    );
  }

  const canPickPeriod = monthOptions.length >= 2;

  return (
    <section
      ref={contentRef}
      className={`relative card-base card-padding space-y-5 ${compact ? 'mb-0' : 'mb-10 md:mb-14'} ${className}`}
    >
      <ChartControls
        componentId={MARKET_PULSE_KPI_ID}
        componentTitle={MARKET_PULSE_KPI_TITLE}
        componentType="kpi"
        dashboardSource={MARKET_PULSE_DASHBOARD_SOURCE}
        relatedLinks={[...MARKET_PULSE_DASHBOARD.relatedLinks]}
        relatedLinksTitle={MARKET_PULSE_DASHBOARD.relatedLinksTitle}
        leadingSlot={
          invoiceCountConfidence ? (
            <ConfidenceIndexBadge
              level={invoiceCountConfidence.level}
              label={invoiceCountConfidence.label}
              detail={invoiceCountConfidence.detail}
            />
          ) : undefined
        }
        onInfo={() => openGuide(CHART_GUIDES.marketPulseKpi)}
        onFavorite={() => handleFavoriteToggle({
          componentId: MARKET_PULSE_KPI_ID,
          componentTitle: MARKET_PULSE_KPI_TITLE,
          dashboardSource: MARKET_PULSE_DASHBOARD_SOURCE,
          componentType: 'kpi',
          description: MARKET_PULSE_KPI.favoriteDescription,
          props: { branchId, extrapolateToFullMonth, compact },
          gridLayout: { containerClass: 'grid grid-cols-1 gap-6' },
        })}
        isFavorited={isFavorited(MARKET_PULSE_KPI_ID)}
        chartRef={contentRef}
        className="mb-1"
      />

      <header className="flex flex-col items-center text-center gap-3 pb-4 border-b border-border-primary">
        <div className="space-y-1.5 min-w-0">
          <h2 className="title-subsection !mb-0">{MARKET_PULSE_KPI_TITLE}</h2>
        </div>

        {canPickPeriod && (
          <div
            className="flex flex-wrap items-end justify-center gap-x-3 gap-y-2 w-full max-w-3xl"
            role="group"
            aria-label={MARKET_PULSE_KPI.periodControlAria}
          >
            <div className="flex w-full sm:w-[17rem] flex-col items-stretch gap-1 text-start">
              <span className="text-xs font-medium text-text-secondary px-0.5">
                {MARKET_PULSE_KPI.endMonth}
              </span>
              <CustomSelect
                options={endMonthOptions}
                value={endSelectValue}
                onChange={handleEndChange}
                placeholder={MARKET_PULSE_KPI.endMonthPlaceholder}
                fullWidth
              />
            </div>
            <span className="text-xs text-text-tertiary shrink-0 pb-2.5 hidden sm:inline">
              {MARKET_PULSE_KPI.relativeTo}
            </span>
            <span className="text-xs text-text-tertiary w-full text-center sm:hidden -my-0.5">
              {MARKET_PULSE_KPI.relativeTo}
            </span>
            <div className="flex w-full sm:w-[17rem] flex-col items-stretch gap-1 text-start">
              <span className="text-xs font-medium text-text-secondary px-0.5">
                {MARKET_PULSE_KPI.startMonth}
              </span>
              <CustomSelect
                options={startMonthOptions}
                value={startSelectValue}
                onChange={handleStartChange}
                placeholder={MARKET_PULSE_KPI.startMonthPlaceholder}
                fullWidth
              />
            </div>
          </div>
        )}

      </header>

      <div className="flex justify-center mb-1 md:mb-2">
        <span className="inline-flex items-center rounded-full bg-lime-300 px-4 py-1.5 text-xs font-bold text-teal-900">
          {MARKET_PULSE_KPI.comparisonBadge}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {[...kpiData.kpis]
          .sort(
            (a, b) =>
              KPI_METRIC_ORDER.indexOf(a.metric.key) - KPI_METRIC_ORDER.indexOf(b.metric.key),
          )
          .map(({ metric, branchGrowth, marketGrowth, lead, status, usesExtrapolation }) => (
            <ComparisonKpiCard
              key={metric.key}
              metricKey={metric.key}
              title={metric.label}
              titleTooltip={metric.tooltip}
              branchGrowth={branchGrowth}
              marketGrowth={marketGrowth}
              lead={lead}
              status={status}
              extrapolationNote={usesExtrapolation ? MARKET_PULSE_LABELS.estimated : undefined}
              className="animate-enter-up"
            />
          ))}
      </div>

      {((kpiData.extrapolate && !kpiData.hasKpiDataNotice) || kpiData.hasKpiDataNotice) && (
        <div className="!mt-8 md:!mt-10 space-y-5">
          {kpiData.extrapolate && !kpiData.hasKpiDataNotice && (
            <p
              className="text-center text-xs text-text-secondary leading-relaxed px-4 py-3 rounded-xl border border-status-warning/20 bg-status-warning-bg"
              role="note"
            >
              {MARKET_PULSE_KPI.estimationNoticePrefix}{' '}
              <strong className="font-semibold text-text-primary">{MARKET_PULSE_LABELS.estimated}</strong>{' '}
              {MARKET_PULSE_KPI.estimationNoticeSuffix}
            </p>
          )}

          {kpiData.hasKpiDataNotice && (
            <KpiDataNoticePanel
              comparisonItems={kpiData.kpiComparisonNoticeItems}
              excludedItems={kpiData.kpiExcludedNoticeItems}
              showEstimationNotice={kpiData.extrapolate}
            />
          )}
        </div>
      )}

    </section>
  );
};

export default MarketPulseKpiSection;
